import type Konva from 'konva';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { hasBgColor } from '~/features/background/pick';
import { useStore } from '~/store';
import { PICK_DUP_COLOR, PICK_OK_COLOR } from './colors';

const LOUPE_SIZE = 132;
const SAMPLE = 11;
const ZOOM = 12;
const READOUT_HEIGHT = 16;
const OFFSET = 16;
const HALF = (SAMPLE - 1) / 2;

type Sample = {
  cx: number;
  cy: number;
  rgb: [number, number, number] | null;
  pixels: Uint8ClampedArray<ArrayBuffer>;
};

let checkerCache: HTMLCanvasElement | null = null;

function getChecker(): HTMLCanvasElement {
  if (checkerCache) return checkerCache;
  const c = document.createElement('canvas');
  c.width = LOUPE_SIZE;
  c.height = LOUPE_SIZE;
  const ctx = c.getContext('2d');
  if (ctx) {
    const cell = 8;
    for (let y = 0; y < LOUPE_SIZE; y += cell) {
      for (let x = 0; x < LOUPE_SIZE; x += cell) {
        ctx.fillStyle = ((x / cell + y / cell) & 1) === 0 ? '#ddd' : '#fff';
        ctx.fillRect(x, y, cell, cell);
      }
    }
  }
  checkerCache = c;
  return c;
}

export function PickLoupe({
  containerRef,
  stageRef,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  stageRef: RefObject<Konva.Stage | null>;
}) {
  const drawMode = useStore((s) => s.editor.drawMode);
  const hasImage = useStore((s) => s.source.imageData !== null);
  const bgColors = useStore((s) => s.background.bgColors);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const tmpCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastPosRef = useRef<{ cx: number; cy: number } | null>(null);
  const pixelsRef = useRef<Uint8ClampedArray<ArrayBuffer> | null>(null);
  const [sample, setSample] = useState<Sample | null>(null);

  useEffect(() => {
    if (drawMode !== 'pickBg' || !hasImage) {
      setSample(null);
      lastPosRef.current = null;
      return;
    }
    const container = containerRef.current;
    const stage = stageRef.current;
    if (!container || !stage) return;

    let rect = container.getBoundingClientRect();
    const refreshRect = () => {
      rect = container.getBoundingClientRect();
    };
    const ro = new ResizeObserver(refreshRect);
    ro.observe(container);

    const onMove = (e: PointerEvent) => {
      stage.setPointersPositions(e);
      const pos = stage.getRelativePointerPosition();
      const imageData = useStore.getState().source.imageData;
      if (!pos || !imageData) {
        setSample(null);
        return;
      }
      const cx = Math.round(pos.x);
      const cy = Math.round(pos.y);
      if (lastPosRef.current?.cx === cx && lastPosRef.current?.cy === cy) return;
      lastPosRef.current = { cx, cy };
      const { data, width, height } = imageData;
      if (!pixelsRef.current) {
        pixelsRef.current = new Uint8ClampedArray(new ArrayBuffer(SAMPLE * SAMPLE * 4));
      }
      const pixels = pixelsRef.current;
      pixels.fill(0);
      for (let dy = -HALF; dy <= HALF; dy++) {
        for (let dx = -HALF; dx <= HALF; dx++) {
          const sx = cx + dx;
          const sy = cy + dy;
          if (sx < 0 || sx >= width || sy < 0 || sy >= height) continue;
          const si = (sy * width + sx) * 4;
          const di = ((dy + HALF) * SAMPLE + (dx + HALF)) * 4;
          pixels[di] = data[si] ?? 0;
          pixels[di + 1] = data[si + 1] ?? 0;
          pixels[di + 2] = data[si + 2] ?? 0;
          pixels[di + 3] = data[si + 3] ?? 0;
        }
      }
      let centre: [number, number, number] | null = null;
      if (cx >= 0 && cx < width && cy >= 0 && cy < height) {
        const ci = (cy * width + cx) * 4;
        if ((data[ci + 3] ?? 0) > 0) {
          centre = [data[ci] ?? 0, data[ci + 1] ?? 0, data[ci + 2] ?? 0];
        }
      }
      setSample({
        cx: e.clientX - rect.left,
        cy: e.clientY - rect.top,
        rgb: centre,
        pixels,
      });
    };

    const onLeave = () => {
      setSample(null);
      lastPosRef.current = null;
    };

    container.addEventListener('pointerenter', refreshRect);
    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);
    window.addEventListener('scroll', refreshRect, true);
    return () => {
      ro.disconnect();
      container.removeEventListener('pointerenter', refreshRect);
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('scroll', refreshRect, true);
    };
  }, [drawMode, hasImage, containerRef, stageRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sample) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (!tmpCanvasRef.current) {
      const t = document.createElement('canvas');
      t.width = SAMPLE;
      t.height = SAMPLE;
      tmpCanvasRef.current = t;
    }
    const tmp = tmpCanvasRef.current;
    const tctx = tmp.getContext('2d');
    if (!tctx) return;
    tctx.putImageData(new ImageData(sample.pixels, SAMPLE, SAMPLE), 0, 0);
    ctx.drawImage(getChecker(), 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tmp, 0, 0, SAMPLE, SAMPLE, 0, 0, LOUPE_SIZE, LOUPE_SIZE);
    const px = HALF * ZOOM;
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.lineWidth = 1;
    ctx.strokeRect(px - 0.5, px - 0.5, ZOOM + 1, ZOOM + 1);
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.strokeRect(px + 0.5, px + 0.5, ZOOM - 1, ZOOM - 1);
  }, [sample]);

  if (!sample) return null;

  const isDup = sample.rgb !== null && hasBgColor(sample.rgb, bgColors);
  const hex = sample.rgb ? rgbToHex(sample.rgb) : '—';
  const swatch = sample.rgb ? `rgb(${sample.rgb.join(',')})` : 'transparent';

  const container = containerRef.current;
  const cw = container?.clientWidth ?? Number.POSITIVE_INFINITY;
  const ch = container?.clientHeight ?? Number.POSITIVE_INFINITY;
  const fullH = LOUPE_SIZE + READOUT_HEIGHT;
  const flipX = sample.cx + OFFSET + LOUPE_SIZE > cw;
  const flipY = sample.cy + OFFSET + fullH > ch;
  const left = flipX ? sample.cx - OFFSET - LOUPE_SIZE : sample.cx + OFFSET;
  const top = flipY ? sample.cy - OFFSET - fullH : sample.cy + OFFSET;

  return (
    <div
      className="pointer-events-none absolute z-20 overflow-hidden rounded shadow-lg"
      style={{
        left,
        top,
        width: LOUPE_SIZE,
        border: `2px solid ${isDup ? PICK_DUP_COLOR : PICK_OK_COLOR}`,
      }}
    >
      <canvas ref={canvasRef} width={LOUPE_SIZE} height={LOUPE_SIZE} className="block" />
      <div
        className="flex items-center gap-1 bg-foreground/90 px-1 font-mono text-[10px] text-background"
        style={{ height: READOUT_HEIGHT }}
      >
        <div
          className="h-3 w-3 flex-shrink-0 rounded-sm border border-background/40"
          style={{ background: swatch }}
        />
        <span>{hex}</span>
        {isDup && <span className="ml-auto">already picked</span>}
      </div>
    </div>
  );
}

function rgbToHex(rgb: readonly [number, number, number]): string {
  return `#${rgb
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}
